import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Plus, Edit, Trash2, ArrowLeft, Upload, X, Image, FolderOpen, Images, Calendar, GripVertical, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const AdminGallery = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventImages, setEventImages] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const [eventFormData, setEventFormData] = useState({
    title: '',
    description: '',
    event_date: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchEvents();
  }, [navigate]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/gallery/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to fetch gallery events');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEventImages = async (eventId) => {
    try {
      const event = events.find(e => e.id === eventId);
      if (event) {
        const response = await axios.get(`${BACKEND_URL}/api/gallery/events/${event.slug}`);
        setEventImages(response.data.images || []);
      }
    } catch (error) {
      toast.error('Failed to fetch images');
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    
    try {
      if (editingEvent) {
        await axios.put(
          `${BACKEND_URL}/api/gallery/events/${editingEvent.id}`,
          eventFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Gallery event updated');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/gallery/events`,
          eventFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Gallery event created');
      }
      setShowEventForm(false);
      setEditingEvent(null);
      resetEventForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save gallery event');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventFormData({
      title: event.title,
      description: event.description || '',
      event_date: event.event_date || ''
    });
    setShowEventForm(true);
  };

  const handleDeleteEvent = async (eventId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/gallery/events/${eventId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Gallery event deleted');
      setShowDeleteConfirm(null);
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(null);
        setEventImages([]);
      }
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleImageUpload = async (e) => {
    if (!selectedEvent) return;
    
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const token = localStorage.getItem('admin_token');
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    try {
      await axios.post(
        `${BACKEND_URL}/api/gallery/events/${selectedEvent.id}/images`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );
      toast.success(`${files.length} image(s) uploaded`);
      fetchEventImages(selectedEvent.id);
      fetchEvents(); // Update image counts
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async (imageId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/gallery/images/${imageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Image deleted');
      fetchEventImages(selectedEvent.id);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleSetCoverImage = async (imagePath) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(
        `${BACKEND_URL}/api/gallery/events/${selectedEvent.id}/cover?image_path=${encodeURIComponent(imagePath)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cover image updated');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update cover image');
    }
  };

  const resetEventForm = () => {
    setEventFormData({
      title: '',
      description: '',
      event_date: ''
    });
  };

  const openEventManager = (event) => {
    setSelectedEvent(event);
    fetchEventImages(event.id);
  };

  const closeEventManager = () => {
    setSelectedEvent(null);
    setEventImages([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-gallery-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            {selectedEvent ? (
              <Button
                onClick={closeEventManager}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="back-to-events-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Events
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                className="border-[#007A7A] text-[#007A7A]"
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            )}
            <h1 className="text-3xl font-bold text-gray-900">
              {selectedEvent ? `Manage: ${selectedEvent.title}` : 'Gallery Manager'}
            </h1>
          </div>
          {!selectedEvent && (
            <Button
              onClick={() => {
                setShowEventForm(true);
                setEditingEvent(null);
                resetEventForm();
              }}
              className="bg-[#007A7A] hover:bg-[#007A7A]/90"
              data-testid="create-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Gallery Event
            </Button>
          )}
        </div>

        {/* Event List View */}
        {!selectedEvent && (
          <div className="space-y-4" data-testid="gallery-events-list">
            {events.length === 0 ? (
              <Card className="py-16">
                <div className="text-center">
                  <FolderOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Gallery Events Yet</h3>
                  <p className="text-gray-500 mb-6">Create your first gallery event to start uploading photos.</p>
                  <Button
                    onClick={() => setShowEventForm(true)}
                    className="bg-[#007A7A] hover:bg-[#007A7A]/90"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Gallery Event
                  </Button>
                </div>
              </Card>
            ) : (
              events.map((event) => (
                <Card key={event.id} className="hover:shadow-lg transition-shadow duration-300" data-testid={`gallery-event-${event.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-6">
                      {/* Cover Image */}
                      <div className="w-32 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                        {event.cover_image ? (
                          <img
                            src={resolveImageUrl(event.cover_image)}
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Images className="w-8 h-8 text-gray-300" />
                          </div>
                        )}
                      </div>

                      {/* Event Info */}
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900 mb-1">{event.title}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {formatDate(event.event_date)}
                          </span>
                          <span className="flex items-center">
                            <Images className="w-4 h-4 mr-1" />
                            {event.image_count || 0} photos
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-gray-600 text-sm line-clamp-1">{event.description}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => openEventManager(event)}
                          className="bg-[#007A7A] hover:bg-[#007A7A]/90"
                          data-testid={`manage-event-${event.id}`}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Manage Photos
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditEvent(event)}
                          className="border-blue-600 text-blue-600"
                          data-testid={`edit-event-${event.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowDeleteConfirm(event)}
                          className="border-red-600 text-red-600 hover:bg-red-50"
                          data-testid={`delete-event-${event.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Event Image Manager View */}
        {selectedEvent && (
          <div className="space-y-6" data-testid="image-manager">
            {/* Upload Section */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Upload Photos</h3>
                    <p className="text-sm text-gray-500">Select multiple images to upload at once (JPG, PNG, WebP - Max 5MB each)</p>
                  </div>
                  <div className="text-sm text-gray-500">
                    {eventImages.length} photo{eventImages.length !== 1 ? 's' : ''} uploaded
                  </div>
                </div>

                <div
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
                    isUploading 
                      ? 'border-gray-300 bg-gray-50' 
                      : 'border-gray-300 hover:border-[#007A7A] hover:bg-[#007A7A]/5'
                  }`}
                >
                  {isUploading ? (
                    <div>
                      <div className="w-full max-w-xs mx-auto bg-gray-200 rounded-full h-3 mb-3">
                        <div 
                          className="bg-[#007A7A] h-3 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-gray-600">Uploading... {uploadProgress}%</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-500">You can select multiple images</p>
                    </>
                  )}
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  className="hidden"
                  data-testid="image-upload-input"
                />
              </CardContent>
            </Card>

            {/* Image Grid */}
            <Card>
              <CardHeader>
                <CardTitle>Gallery Photos</CardTitle>
              </CardHeader>
              <CardContent>
                {eventImages.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Images className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p>No photos uploaded yet. Use the upload section above to add photos.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="image-grid">
                    {eventImages.map((image) => (
                      <div 
                        key={image.id} 
                        className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100"
                        data-testid={`gallery-image-${image.id}`}
                      >
                        <img
                          src={resolveImageUrl(image.image_path)}
                          alt=""
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => setLightboxImage(image)}
                        />
                        
                        {/* Overlay Actions */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleSetCoverImage(image.image_path)}
                            className="bg-white/90 hover:bg-white text-gray-900"
                            title="Set as cover"
                            data-testid={`set-cover-${image.id}`}
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleDeleteImage(image.id)}
                            className="bg-red-500/90 hover:bg-red-600 text-white"
                            title="Delete"
                            data-testid={`delete-image-${image.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Cover Badge */}
                        {selectedEvent.cover_image === image.image_path && (
                          <div className="absolute top-2 left-2 bg-[#007A7A] text-white text-xs px-2 py-1 rounded-full flex items-center">
                            <Star className="w-3 h-3 mr-1" /> Cover
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Create/Edit Event Dialog */}
        <Dialog open={showEventForm} onOpenChange={setShowEventForm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingEvent ? 'Edit Gallery Event' : 'Create Gallery Event'}</DialogTitle>
              <DialogDescription>
                {editingEvent ? 'Update the details of this gallery event.' : 'Create a new gallery event to organize your photos.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateEvent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event Title *</label>
                <input
                  type="text"
                  value={eventFormData.title}
                  onChange={(e) => setEventFormData({ ...eventFormData, title: e.target.value })}
                  required
                  placeholder="e.g., Summer Community Picnic 2025"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A] focus:border-transparent"
                  data-testid="event-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event Date</label>
                <input
                  type="date"
                  value={eventFormData.event_date}
                  onChange={(e) => setEventFormData({ ...eventFormData, event_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A] focus:border-transparent"
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={eventFormData.description}
                  onChange={(e) => setEventFormData({ ...eventFormData, description: e.target.value })}
                  rows="3"
                  placeholder="Brief description of the event..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007A7A] focus:border-transparent"
                  data-testid="event-description-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEventForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#007A7A] hover:bg-[#007A7A]/90" data-testid="save-event-btn">
                  {editingEvent ? 'Update Event' : 'Create Event'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Gallery Event?</DialogTitle>
              <DialogDescription>
                This will permanently delete "{showDeleteConfirm?.title}" and all {showDeleteConfirm?.image_count || 0} photos. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDeleteEvent(showDeleteConfirm.id)}
                data-testid="confirm-delete-btn"
              >
                Delete Event
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Lightbox */}
        <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
          <DialogContent className="max-w-4xl bg-black/95 border-none p-0">
            <div className="relative">
              <button
                onClick={() => setLightboxImage(null)}
                className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 rounded-full p-2 text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              {lightboxImage && (
                <img
                  src={resolveImageUrl(lightboxImage.image_path)}
                  alt=""
                  className="w-full h-auto max-h-[80vh] object-contain"
                />
              )}
              {/* Navigation arrows */}
              {eventImages.length > 1 && lightboxImage && (
                <>
                  <button
                    onClick={() => {
                      const currentIndex = eventImages.findIndex(img => img.id === lightboxImage.id);
                      const prevIndex = currentIndex > 0 ? currentIndex - 1 : eventImages.length - 1;
                      setLightboxImage(eventImages[prevIndex]);
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-2 text-white transition-colors"
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                  <button
                    onClick={() => {
                      const currentIndex = eventImages.findIndex(img => img.id === lightboxImage.id);
                      const nextIndex = currentIndex < eventImages.length - 1 ? currentIndex + 1 : 0;
                      setLightboxImage(eventImages[nextIndex]);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 rounded-full p-2 text-white transition-colors"
                  >
                    <ChevronRight className="w-8 h-8" />
                  </button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminGallery;
